<template>
  <div class="wg-modal-back" :class="{ closing: closing }" @click="close">
    <div
      class="wg-modal wg-terms-modal"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="doc === 'policy' ? 'wg-policy-title' : 'wg-terms-title'"
      @click.stop
    >
      <button class="wg-modal-x" aria-label="Close" @click="close">
        <i class="material-icons">close</i>
      </button>

      <div class="wg-modal-head">
        <div class="wg-modal-ico brand"><i class="material-icons">check</i></div>
        <div>
          <h3 :id="doc === 'policy' ? 'wg-policy-title' : 'wg-terms-title'">{{ document.title }}</h3>
        </div>
      </div>

      <div class="wg-terms-body">
        <p v-if="document.intro">{{ document.intro }}</p>
        <section v-for="section in document.sections" :key="section.h">
          <h4>{{ section.h }}</h4>
          <template v-if="section.paras">
            <p v-for="(text, i) in section.paras" :key="'p' + i">{{ text }}</p>
          </template>
          <ul v-if="section.items">
            <li v-for="(text, i) in section.items" :key="'i' + i">{{ text }}</li>
          </ul>
          <ol v-if="section.numbered">
            <li v-for="(text, i) in section.numbered" :key="'n' + i">{{ text }}</li>
          </ol>
          <template v-if="section.after">
            <p v-for="(text, i) in section.after" :key="'a' + i">{{ text }}</p>
          </template>
          <ul v-if="section.items2">
            <li v-for="(text, i) in section.items2" :key="'j' + i">{{ text }}</li>
          </ul>
        </section>
        <p class="wg-terms-updated">{{ document.updated }}</p>
      </div>

      <div class="acts">
        <button class="wg-mbtn solid" @click="close">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Performance Monitoring legal documents (design "POP White Glove"
 * white-glove/terms.jsx): the Terms & Conditions and the Data Access
 * Policy, opened from the consent checkbox links in the GSC wizard.
 */
const TERMS = {
  title: 'PageOptimizer Pro - Performance Monitoring Terms & Conditions',
  intro: 'These Performance Monitoring Terms & Conditions (“Monitoring Terms”) govern your use of the ongoing performance monitoring feature (“Performance Monitoring”) offered by PageOptimizer Pro LLC, a Wyoming limited liability company (“PageOptimizer Pro”, “POP”, “we”, “us” or “our”). They add to, and form part of, our Terms of Service (https://www.pageoptimizer.pro/terms-of-service) and Privacy Policy (https://www.pageoptimizer.pro/privacy-policy). Where these Monitoring Terms conflict with the Terms of Service in relation to Performance Monitoring, these Monitoring Terms apply. Capitalised terms not defined here have the meaning given in the Terms of Service.',
  sections: [
    { h: '1. What Performance Monitoring does',
      items: [
        'Performance Monitoring connects your Google Search Console account to POP so we can track how your verified domains perform in Google Search over time.',
        'Once connected, we retrieve a defined set of Search Console performance data on a recurring basis (currently reviewed quarterly for each connected domain) and use it to generate performance reports and “Wins” reports inside your POP account.',
        'Performance Monitoring is optional. You can choose not to enable it, and you can turn it off at any time (see section 7). If you do not enable it, or later turn it off, the related reports will pause.',
      ] },
    { h: '2. Connecting your Google account (read-only access)',
      items: [
        'To use Performance Monitoring you connect your Google account through Google’s standard authorisation (OAuth) screen and grant POP read-only access to your Google Search Console data.',
        'Read-only means we can read the performance data described in our Performance Monitoring Data Access Policy. We do not request permission to change, publish or delete anything in your Google Search Console or your wider Google account.',
        'We only access Google Search Console data. We do not access any other data from your Google account.',
      ] },
    { h: '3. Your authority to connect a domain',
      items: [
        'You may only connect a domain through Performance Monitoring if you own it or are otherwise authorised to access and share its Search Console data.',
        'If you connect a domain that belongs to a client or third party, you confirm that you have that party’s authority to connect the domain, to allow us to access and store its Search Console data, and to agree to these Monitoring Terms and the Data Access Policy on their behalf.',
        'You are responsible for making sure any client or third party is informed about how their data will be accessed and stored, as described in the Data Access Policy.',
      ] },
    { h: '4. Your consent',
      items: [
        'By ticking the consent box and completing setup, you authorise PageOptimizer Pro to access, retrieve and store the Google Search Console performance data described in the Data Access Policy, on an ongoing basis, for the purpose of providing Performance Monitoring.',
        'This authorisation continues until you withdraw it or your access to Performance Monitoring ends.',
      ] },
    { h: '5. How we may use the data',
      items: [
        'We use the connected Search Console data to provide Performance Monitoring to you: to calculate performance changes, generate reports, and surface “Wins”.',
        'We may create anonymised, aggregated data from Search Console data and use it for our own analysis and marketing. Anonymised, aggregated data does not identify you, your domain or any individual.',
        'We will not sell your Search Console data, share it outside our organisation except with the service providers needed to run the feature, use it to serve advertising, or use it to train or improve our Rank Engine or any general AI or machine-learning models. Full detail is in the Data Access Policy.',
      ] },
    { h: '6. Data we access and how we handle it',
      items: [
        'The categories of data we access, where we store it, how long we keep it and your rights are all set out in the Performance Monitoring Data Access Policy, which forms part of these Monitoring Terms.',
      ] },
    { h: '7. Turning off Performance Monitoring and withdrawing consent',
      items: [
        'You can withdraw your consent and turn off Performance Monitoring at any time. You can withdraw your consent and turn off Performance Monitoring at any time, in either of two ways:',
      ],
      numbered: [
        'In POP. Open your project, go to Settings, and under Google Search Console click Disconnect. Performance Monitoring stops immediately, automated tracking and Wins reports pause, and POP no longer accesses your Search Console data. Data already collected is kept in your project until you delete the project.',
        "In your Google account. Go to your Google Account permissions page (myaccount.google.com/permissions), select PageOptimizer Pro, and click Remove access. This revokes POP's access token, and Performance Monitoring stops the next time POP attempts to check your data. Your POP project will show Search Console as disconnected.",
      ],
      after: [
        'You can reconnect at any time from Settings. Withdrawing consent does not affect other POP features that do not rely on Search Console data.',
      ],
      items2: [
        'Withdrawing consent is as easy as giving it. When you withdraw consent, we stop retrieving new Search Console data and Performance Monitoring reports pause.',
        'Search Console data we have already retrieved is not deleted automatically when you withdraw consent. It stays in your project so your historical Wins reports and tracked URLs remain visible, and monitoring can resume without gaps if you reconnect. We do not use retained data for any new purpose after withdrawal. You can delete this data at any time by deleting the project in POP, or by contacting us at support@pageoptimizer.pro and asking us to delete it.',
      ] },
    { h: '8. Availability and accuracy',
      items: [
        'Performance Monitoring relies on data provided by Google Search Console and on Google’s systems being available. We do not control that data or those systems and cannot guarantee they will always be accurate, complete or available.',
        'Performance Monitoring reports are provided for information only. They are not a guarantee of rankings, traffic or any specific result.',
      ] },
    { h: '9. Suspension, changes and termination',
      items: [
        'We may suspend, change or withdraw Performance Monitoring, or these Monitoring Terms, in line with our Terms of Service. If we make a material change to how we handle your Search Console data, we will tell you and, where required, ask for your consent again.',
      ] },
    { h: '10. How these terms fit with our other terms',
      items: [
        'These Monitoring Terms are governed by, and interpreted in line with, our Terms of Service - including its provisions on liability, disclaimers and governing law. Please read them together.',
      ] },
    { h: '11. Contact',
      items: [
        'Questions about these Monitoring Terms: support@pageoptimizer.pro.',
      ] },
  ],
  updated: 'Last updated: September 8, 2026',
}

const POLICY = {
  title: 'PageOptimizer Pro - Performance Monitoring Data Access Policy',
  sections: [
    { h: '1. What this policy covers',
      paras: ["This policy explains what data we access, use and store when you connect Google Search Console to PageOptimizer Pro's Performance Monitoring feature, and what your rights are. It sits alongside our main Privacy Policy (https://www.pageoptimizer.pro/privacy-policy)."] },
    { h: '2. Who we are',
      paras: ['PageOptimizer Pro LLC, a Wyoming limited liability company. Contact: support@pageoptimizer.pro. If you connect a domain you own, we are the data controller. If you connect a domain on behalf of a client, you are the controller and we process the data on your instructions.'] },
    { h: '3. What data we access',
      paras: [
        'Through a read-only Google Search Console connection, we access and store, for each connected domain: search queries, pages, clicks, impressions, click-through rate and average position. We retrieve this data on a recurring basis. We cannot change, add to or delete anything in your Search Console.',
        'This data is supplied by Google in aggregated form. Google removes rare queries and queries that may contain personal information before they reach us, so it does not identify the individuals who performed the searches. The only personal data we process is your Google account identity and the access credentials needed to keep the connection working.',
      ] },
    { h: '4. Why we use it',
      paras: ['To provide Performance Monitoring: measuring how your connected domains perform over time, generating performance and Wins reports, and supporting you with the feature.'] },
    { h: '5. Legal basis',
      paras: ['If you are in the EEA or UK, our legal basis is your consent, given when you tick the consent box and enable Performance Monitoring. You can withdraw it at any time (section 9).'] },
    { h: '6. Google user data and Limited Use',
      paras: ["PageOptimizer Pro's use of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements. We use Search Console data only to provide and improve Performance Monitoring. We do not sell it, use it for advertising, transfer it to third parties except the providers needed to run the feature, or use it to train generalised AI or machine-learning models. Our staff do not read your Search Console data except with your permission, for security purposes, or where required by law."] },
    { h: '7. Where we store it',
      paras: ['In our application database, hosted by Hetzner Online GmbH on servers in Ashburn, Virginia, United States. If you are in the EEA or UK, your data is transferred to the United States under the Standard Contractual Clauses (and UK Addendum) in our agreement with Hetzner.'] },
    { h: '8. How long we keep it',
      paras: ['While Performance Monitoring is connected, we keep the Search Console data we retrieve for as long as your project exists, so that your Wins reports and tracking history remain available. When you turn off Performance Monitoring, withdraw consent or revoke access, we stop retrieving new data, but data already retrieved is not deleted automatically and stays in your project. You can delete it at any time by deleting the project in POP, or by contacting us at support@pageoptimizer.pro. If you close your POP account, all your projects and the Search Console data within them are deleted.'] },
    { h: '9. Revoking access',
      paras: ['You can revoke access at any time, in either of two ways:'],
      items: [
        'In POP: open your project, go to Settings, and under Google Search Console click Disconnect. We stop retrieving data immediately and reports pause.',
        'In Google: go to myaccount.google.com/permissions, select PageOptimizer Pro and click Remove access. Monitoring stops the next time POP attempts to retrieve data.',
      ],
      after: ['Revoking access does not delete data already retrieved (see section 8). You can reconnect at any time from Settings.'] },
    { h: '10. Service providers',
      paras: ['Google LLC (United States), as the source of the data, and Hetzner Online GmbH (Germany), which hosts our application and database in the United States. Both are bound by contract to protect the data.'] },
    { h: '11. Security',
      paras: ['Data is encrypted in transit. Access is limited to staff who need it to operate, support and troubleshoot Performance Monitoring.'] },
    { h: '12. Your rights',
      paras: ['If you are in the EEA or UK you may access, correct, erase, restrict, object to the processing of, or receive a copy of your data, and withdraw consent at any time. Contact support@pageoptimizer.pro. You may also complain to your local data protection authority.'] },
    { h: '13. Changes',
      paras: ['If we make a material change to how we handle your Search Console data, we will tell you and, where required, ask for your consent again.'] },
    { h: '14. Contact',
      paras: ['support@pageoptimizer.pro'] },
  ],
  updated: 'Last modified: 09/09/2026',
}

export default {
  name: 'WgTermsModal',
  props: {
    /** 'terms' or 'policy' */
    doc: {
      type: String,
      default: 'terms',
    },
  },
  data () {
    return {
      closing: false,
    }
  },
  computed: {
    document () {
      return this.doc === 'policy' ? POLICY : TERMS
    },
  },
  mounted () {
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
    clearTimeout(this.closeTimer)
  },
  methods: {
    close () {
      // play the open animation in reverse, then let the parent unmount us
      if (this.closing) return
      this.closing = true
      this.closeTimer = setTimeout(() => this.$emit('close'), 170)
    },
    onKeydown (event) {
      if (event.key === 'Escape') this.close()
    },
  },
}
</script>

<style lang="scss" scoped>
@import './settings-card.scss';

.wg-modal-back {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 10, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  padding: 24px;
  animation: wgfade 0.15s ease;

  &.closing {
    animation: wgfade 0.15s ease reverse forwards;
  }
}

.wg-modal {
  position: relative;
  background: #fff;
  border-radius: 14px;
  box-shadow: $shadow-menu;
  width: 100%;
  padding: 28px 30px 24px;
  animation: wgpop 0.18s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}

.closing .wg-modal {
  animation: wgpop 0.15s ease reverse forwards;
}

@keyframes wgfade {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes wgpop {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.wg-terms-modal {
  max-width: 720px;
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
}

.wg-modal-x {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: $pop-ink-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    background: $pop-box-bg;
    color: $pop-teal;
  }

  .material-icons {
    font-size: 1.2rem;
  }
}

.wg-modal-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 16px;
  padding-right: 26px;

  h3 {
    font-size: 1.1rem;
    font-weight: 700;
    color: $pop-teal;
    margin: 0;
  }
}

.wg-modal-ico {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  &.brand {
    background: $pop-accent-bg;
    color: $pop-accent;
  }

  .material-icons {
    font-size: 1.5rem;
  }
}

.wg-terms-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border: 1px solid $wg-hairline;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 20px;
  font-size: 0.85rem;
  line-height: 1.55;
  color: #363636;

  p {
    margin: 0 0 12px;
  }

  section {
    margin-top: 14px;
  }

  h4 {
    font-size: 0.92rem;
    font-weight: 700;
    color: $pop-teal;
    margin: 0 0 6px;
  }

  ul,
  ol {
    margin: 0 0 10px;
    padding-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  ul {
    list-style: disc;
  }

  ol {
    list-style: decimal;
  }
}

.wg-terms-updated {
  color: $pop-ink-muted;
  font-size: 0.78rem;
  margin-top: 16px;
}

.acts {
  display: flex;
  justify-content: flex-end;
}

.wg-mbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 24px;
  border-radius: 30px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;

  &.solid {
    background: $pop-accent;
    color: #fff;

    &:hover {
      opacity: 0.85;
    }
  }
}
</style>
